import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { faCreditCard, faUniversity } from "@fortawesome/pro-solid-svg-icons";
import WePayACHPlaidModal from "Payments/ManagePaymentMethods/WePayACHPlaidModal";
import { LeadText } from "Payments/PaymentPage/PaymentFormScreen/styles";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { useBankProvider } from "Payments/globalPaymentsStateHooks";
import { motion } from "framer-motion";

import FnrButton, { FnrButtonContent } from "UI/FnrButton3";

import { useWindowSize } from "Utilities/Hooks/useWindowSize";

import { usePaymentForm } from "../../PaymentFormContext";
import { useInitialPaymentType } from "../../paymentFormStateHooks";

export default function ChooseMethodType() {
  const {
    state: {
      tabID,
      network,
      user,
      userInitial,
      fund,
      authCodeData,
      selectedPaymentMethodID,
    },
    dispatch,
  } = usePaymentPage();
  const {
    state: { hasAmountURLParam },
    dispatch: formDispatch,
  } = usePaymentForm();
  const windowSize = useWindowSize();
  const { isACHAllowed, isCardAllowed } = network;
  const { fundColorSafe } = fund;
  const loginLevelInitial = userInitial.loginLevel;
  const isFullyLoggedIn = user.loginLevel === "full";
  // phone and email values are only needed to be submitted for users who are not fully logged in.
  const phone =
    !isFullyLoggedIn && authCodeData?.loginValueType === "phone"
      ? authCodeData.loginValue
      : null;
  const email =
    !isFullyLoggedIn && authCodeData?.loginValueType === "email"
      ? authCodeData.loginValue
      : null;
  const { providerIsStax } = useBankProvider();

  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const { setInitialPaymentType } = useInitialPaymentType();

  const isOnScreenInitially =
    hasAmountURLParam && loginLevelInitial === "full" && isACHAllowed;

  useEffect(
    function pushToCardIfOnlyOption() {
      if (!isACHAllowed) {
        formDispatch({
          type: "setFormStage",
          stage: "paymentInfo",
          value: "addCard",
        });
      }
    },
    [isACHAllowed, formDispatch]
  );

  useEffect(
    function checkIfShouldPushToMethodSelected() {
      if (selectedPaymentMethodID) {
        formDispatch({
          type: "setFormStage",
          stage: "paymentInfo",
          value: "methodSelected",
        });
      }
    },
    [selectedPaymentMethodID, formDispatch]
  );

  const handleBankClick = () => {
    if (!providerIsStax) {
      setShowPlaidModal(true);
      dispatch({ type: "setEditingRecurringPayment", payload: false });
      return;
    }

    setInitialPaymentType("bank");
    formDispatch({
      type: "setFormStage",
      stage: "paymentInfo",
      value: "addCard",
    });
    dispatch({ type: "setEditingRecurringPayment", payload: false });
  };

  const handleCardClick = () => {
    formDispatch({
      type: "setFormStage",
      stage: "paymentInfo",
      value: "addCard",
    });
    dispatch({ type: "setEditingRecurringPayment", payload: false });
  };

  const handleSuccessfulPlaidSave = (res) => {
    dispatch({
      type: "setSelectedPaymentMethodID",
      payload: res.newPaymentMethodID,
    });
    dispatch({
      type: "setPaymentMethods",
      payload: res.paymentMethods,
    });
  };

  if (!isACHAllowed) return null;

  const typeWrapperInitial = isOnScreenInitially
    ? { opacity: 1, y: 0 }
    : { opacity: 0, y: 20 };
  const typeWrapperAnimate = { opacity: 1, y: 0 };

  return (
    <Wrapper>
      <LeadText>How would you like to pay?</LeadText>
      <MethodTypes>
        <MethodTypeWrapper
          initial={typeWrapperInitial}
          animate={typeWrapperAnimate}
          transition={{ delay: 0.3 }}
        >
          <FnrButton
            data-cy="fund-it-pay-with-bank"
            onClick={handleBankClick}
            color={fundColorSafe}
            fullWidth={windowSize.width < 410}
          >
            <FnrButtonContent>
              <FnrButtonContentInner>
                <Icon>
                  <FontAwesomeIcon icon={faUniversity} />
                </Icon>
                <Label>Bank Account</Label>
              </FnrButtonContentInner>
            </FnrButtonContent>
          </FnrButton>

          <WePayACHPlaidModal
            show={showPlaidModal}
            setShow={setShowPlaidModal}
            onSave={handleSuccessfulPlaidSave}
            shouldSavePaymentMethod={user.loginLevel === "full"}
            tabID={tabID}
            email={email}
            phone={phone}
            userID={user?.ID}
          />
        </MethodTypeWrapper>

        {isCardAllowed ? (
          <MethodTypeWrapper
            initial={typeWrapperInitial}
            animate={typeWrapperAnimate}
            transition={{ delay: 0.4 }}
          >
            <FnrButton
              onClick={handleCardClick}
              color={fundColorSafe}
              fullWidth={windowSize.width < 410}
              data-cy="fund-it-pay-with-card"
            >
              <FnrButtonContent>
                <FnrButtonContentInner>
                  <Icon>
                    <FontAwesomeIcon icon={faCreditCard} />
                  </Icon>
                  <Label>Credit or Debit Card</Label>
                </FnrButtonContentInner>
              </FnrButtonContent>
            </FnrButton>
          </MethodTypeWrapper>
        ) : null}
      </MethodTypes>
    </Wrapper>
  );
}

const Wrapper = styled.div``;

const MethodTypes = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;

  @media (max-width: 410px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const MethodTypeWrapper = styled(motion.div)`
  flex: 1;
  display: flex;
  justify-content: center;

  @media (max-width: 410px) {
    width: 100%;
  }
`;

const FnrButtonContentInner = styled.div``;

const Icon = styled.div`
  text-align: center;
  font-size: 32px;
`;

const Label = styled.div`
  margin-top: 8px;
  text-align: center;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.2;
  text-transform: uppercase;

  @media (max-width: 440px) {
    font-size: 13px;
  }
`;
